import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import axios from 'axios'
import { vesselGroups } from '../data/vesselGroups'

mapboxgl.accessToken = 'pk.eyJ1IjoiZm9yYWdlZmlzaCIsImEiOiJjbTZreHgzNHIwMG8xMmpxeHE3OWNjemJlIn0.Gy8UzHPahz3h6tGKsNMqJQ'

const GFW_API_BASE = 'https://gateway.api.globalfishingwatch.org'
const GFW_TOKEN = import.meta.env.VITE_GFW_API_TOKEN

const S = {
  shell: { background: '#0a0a0a', color: '#e0e0e0', minHeight: '100%' },
  statsRow: { display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  statCard: {
    background: '#111',
    border: '1px solid #1e1e1e',
    borderRadius: '8px',
    padding: '1rem 1.5rem',
    flex: '1',
    minWidth: '140px',
  },
  statValue: { fontSize: '1.8rem', fontWeight: 700, color: '#B8860B', lineHeight: 1 },
  statLabel: { fontSize: '0.75rem', color: '#bbb', marginTop: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.08em' },
  filtersRow: { display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  filterBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.55rem 1rem',
    borderRadius: '6px',
    border: '1px solid',
    fontSize: '0.82rem',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'opacity 0.15s',
    background: 'transparent',
  },
  filterDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  mapWrapper: { borderRadius: '8px', overflow: 'hidden', border: '1px solid #1e1e1e' },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '1rem',
  },
  modal: {
    background: '#111', border: '1px solid #2a2a2a', borderRadius: '10px',
    maxWidth: '480px', width: '100%', maxHeight: '80vh', overflowY: 'auto',
  },
  modalHeader: {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #1e1e1e',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  },
  modalTitle: { fontSize: '1.1rem', fontWeight: 700, color: '#e0e0e0', margin: 0 },
  modalSubtitle: { fontSize: '0.78rem', color: '#bbb', marginTop: '0.2rem' },
  modalBody: { padding: '1.25rem 1.5rem' },
  closeBtn: {
    background: 'none', border: 'none', color: '#999', cursor: 'pointer',
    fontSize: '1.2rem', padding: '0.2rem', lineHeight: 1,
  },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
  fieldLabel: { fontSize: '0.72rem', color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.2rem' },
  fieldValue: { fontSize: '0.9rem', color: '#ccc', fontFamily: 'monospace' },
  gfwLink: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    marginTop: '1rem', padding: '0.5rem 1rem',
    background: '#8B1A1A', color: '#fff', borderRadius: '5px',
    textDecoration: 'none', fontSize: '0.82rem', fontWeight: 500,
  },
  loadingOverlay: {
    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
  },
  loadingBox: {
    background: '#111', border: '1px solid #2a2a2a', borderRadius: '8px',
    padding: '1.5rem 2rem', textAlign: 'center',
  },
  spinner: {
    width: '40px', height: '40px', border: '3px solid #1e1e1e',
    borderTop: '3px solid #B8860B', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem',
  },
}

export default function VesselMap() {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const markers = useRef([])
  const [vesselPositions, setVesselPositions] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedVessel, setSelectedVessel] = useState(null)
  const [filters, setFilters] = useState({
    'gulf-menhaden': true,
    'atlantic-menhaden': true,
    'atlantic-herring': true,
  })

  const totalVessels = Object.values(vesselGroups).reduce((sum, g) => sum + g.vessels.length, 0)
  const activeVessels = Object.entries(filters).reduce((sum, [id, on]) => {
    if (!on) return sum
    const g = Object.values(vesselGroups).find(g => g.id === id)
    return sum + (g?.vessels.length || 0)
  }, 0)

  const toggleFilter = (id) => setFilters(prev => ({ ...prev, [id]: !prev[id] }))

  // Init map
  useEffect(() => {
    if (map.current) return

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-80, 34],
      zoom: 4,
      projection: 'mercator',
    })

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right')
    map.current.addControl(new mapboxgl.ScaleControl(), 'bottom-left')

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [])

  // Generate fallback positions
  const generateVesselPosition = (vessel) => {
    if (vessel.groupId === 'gulf-menhaden') return { lng: -88 + Math.random() * 10, lat: 28 + Math.random() * 4 }
    if (vessel.groupId === 'atlantic-menhaden') return { lng: -76 + Math.random() * 2, lat: 36 + Math.random() * 3 }
    if (vessel.groupId === 'atlantic-herring') return { lng: -70 + Math.random() * 5, lat: 39 + Math.random() * 5 }
    return { lng: -75, lat: 38 }
  }

  // Fetch positions
  useEffect(() => {
    const fetchPositions = async () => {
      setLoading(true)

      const activeGroups = Object.values(vesselGroups).filter(g => filters[g.id])
      const allVessels = activeGroups.flatMap(g =>
        g.vessels.map(v => ({ ...v, groupId: g.id, groupColor: g.color, groupName: g.name }))
      )

      if (allVessels.length === 0) {
        setVesselPositions([])
        setLoading(false)
        return
      }

      const endDate = new Date().toISOString().split('T')[0]
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      const batchSize = 5
      const positions = []

      for (let i = 0; i < allVessels.length; i += batchSize) {
        const batch = allVessels.slice(i, i + batchSize)

        const batchResults = await Promise.all(batch.map(async vessel => {
          try {
            const searchRes = await axios.get(`${GFW_API_BASE}/v3/vessels/search`, {
              params: { query: vessel.mmsi, 'datasets[0]': 'public-global-vessel-identity:latest' },
              headers: { Authorization: `Bearer ${GFW_TOKEN}` },
            })

            if (!searchRes.data?.entries?.length) {
              return { ...vessel, ...generateVesselPosition(vessel), lastSeen: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000), dataSource: 'simulated' }
            }

            const vesselId = searchRes.data.entries[0].id
            const eventsRes = await axios.get(`${GFW_API_BASE}/v3/events`, {
              params: {
                'vessels[0]': vesselId,
                'datasets[0]': 'public-global-fishing-events:latest',
                'start-date': startDate,
                'end-date': endDate,
              },
              headers: { Authorization: `Bearer ${GFW_TOKEN}` },
            })

            let lat, lng, lastSeen
            if (eventsRes.data?.entries?.length > 0) {
              const ev = eventsRes.data.entries[0]
              lat = ev.position?.lat
              lng = ev.position?.lon
              lastSeen = new Date(ev.start)
            }

            if (!lat || !lng) {
              const fb = generateVesselPosition(vessel)
              lat = fb.lat; lng = fb.lng
              lastSeen = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000)
            }

            return { ...vessel, lat, lng, lastSeen, dataSource: eventsRes.data?.entries?.length > 0 ? 'gfw-api' : 'simulated' }
          } catch {
            return { ...vessel, ...generateVesselPosition(vessel), lastSeen: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000), dataSource: 'simulated' }
          }
        }))

        positions.push(...batchResults.filter(Boolean))
        if (i + batchSize < allVessels.length) {
          await new Promise(r => setTimeout(r, 500))
        }
      }

      setVesselPositions(positions)
      setLoading(false)
    }

    fetchPositions()
  }, [filters])

  // Render markers
  useEffect(() => {
    if (!map.current || !vesselPositions.length) return

    markers.current.forEach(m => m.remove())
    markers.current = []

    vesselPositions.forEach(vessel => {
      const el = document.createElement('div')
      el.style.cssText = `
        width: 20px; height: 20px; border-radius: 50%;
        background-color: ${vessel.groupColor};
        border: 2px solid rgba(255,255,255,0.6);
        box-shadow: 0 2px 6px rgba(0,0,0,0.5);
        cursor: pointer; transition: box-shadow 0.2s;
      `
      el.addEventListener('mouseenter', () => {
        el.style.boxShadow = `0 0 0 4px ${vessel.groupColor}50, 0 2px 10px rgba(0,0,0,0.5)`
      })
      el.addEventListener('mouseleave', () => {
        el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.5)'
      })

      const popup = new mapboxgl.Popup({ offset: 22, closeButton: false })
        .setHTML(`
          <div style="padding:8px; min-width:180px; font-family:DM Sans,sans-serif;">
            <div style="font-weight:700; font-size:13px; color:${vessel.groupColor}; margin-bottom:3px;">${vessel.name}</div>
            <div style="font-size:11px; color:#999; margin-bottom:6px;">${vessel.groupName}</div>
            <div style="font-size:11px; color:#ccc;">
              <div>MMSI: ${vessel.mmsi}</div>
              ${vessel.imo ? `<div>IMO: ${vessel.imo}</div>` : ''}
              <div style="margin-top:3px; font-style:italic; color:#888;">
                Last seen: ${vessel.lastSeen ? vessel.lastSeen.toLocaleDateString() : 'Unknown'}
              </div>
              ${vessel.dataSource === 'gfw-api'
                ? '<div style="margin-top:4px; color:#10b981; font-size:10px;">&#10003; Real AIS data</div>'
                : '<div style="margin-top:4px; color:#B8860B; font-size:10px;">&#9651; Simulated position</div>'
              }
            </div>
            <div style="margin-top:6px; font-size:11px; color:#8B1A1A; cursor:pointer;">Click for details &rarr;</div>
          </div>
        `)

      const marker = new mapboxgl.Marker(el)
        .setLngLat([vessel.lng, vessel.lat])
        .setPopup(popup)
        .addTo(map.current)

      el.addEventListener('click', () => setSelectedVessel(vessel))
      markers.current.push(marker)
    })

    if (vesselPositions.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      vesselPositions.forEach(v => bounds.extend([v.lng, v.lat]))
      map.current.fitBounds(bounds, { padding: 60, maxZoom: 7 })
    }
  }, [vesselPositions])

  return (
    <div style={S.shell}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Stats */}
      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={S.statValue}>{totalVessels}</div>
          <div style={S.statLabel}>Total Vessels Tracked</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statValue}>{activeVessels}</div>
          <div style={S.statLabel}>Showing on Map</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statValue}>3</div>
          <div style={S.statLabel}>Fleet Types</div>
        </div>
        <div style={{ ...S.statCard, flex: 2 }}>
          <div style={{ fontSize: '0.82rem', color: '#bbb', lineHeight: 1.5 }}>
            AIS tracking via Global Fishing Watch. Positions update ~every 3 days.
            Vessels may disable transponders — actual activity may differ.
          </div>
        </div>
      </div>

      {/* Fleet filters */}
      <div style={S.filtersRow}>
        {Object.values(vesselGroups).map(group => (
          <button
            key={group.id}
            onClick={() => toggleFilter(group.id)}
            style={{
              ...S.filterBtn,
              borderColor: filters[group.id] ? group.color : '#2a2a2a',
              color: filters[group.id] ? '#e0e0e0' : '#d8d8d8',
              opacity: filters[group.id] ? 1 : 0.55,
              background: filters[group.id] ? `${group.color}12` : 'transparent',
            }}
          >
            <span style={{ ...S.filterDot, background: group.color, opacity: filters[group.id] ? 1 : 0.4 }} />
            {group.name}
            <span style={{ color: '#bbb', fontSize: '0.75rem' }}>({group.vessels.length})</span>
          </button>
        ))}
      </div>

      {/* Map */}
      <div style={{ ...S.mapWrapper, position: 'relative' }}>
        <div ref={mapContainer} style={{ width: '100%', height: '600px' }} />

        {loading && (
          <div style={S.loadingOverlay}>
            <div style={S.loadingBox}>
              <div style={S.spinner} />
              <div style={{ fontSize: '0.9rem', color: '#ccc' }}>Loading vessel positions...</div>
              <div style={{ fontSize: '0.78rem', color: '#bbb', marginTop: '0.3rem' }}>Querying Global Fishing Watch</div>
            </div>
          </div>
        )}

        {!loading && vesselPositions.length === 0 && (
          <div style={{ ...S.loadingOverlay, background: 'rgba(0,0,0,0.4)' }}>
            <div style={S.loadingBox}>
              <div style={{ fontSize: '0.9rem', color: '#ccc' }}>No vessels to display.</div>
              <div style={{ fontSize: '0.78rem', color: '#bbb', marginTop: '0.3rem' }}>Enable at least one fleet filter above.</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ fontSize: '0.75rem', color: '#bbb', marginTop: '0.75rem' }}>
        Data: <a href="https://globalfishingwatch.org" target="_blank" rel="noopener noreferrer" style={{ color: '#8B1A1A' }}>Global Fishing Watch</a> AIS transmissions
      </div>

      {/* Vessel detail modal */}
      {selectedVessel && (
        <div style={S.modalOverlay} onClick={() => setSelectedVessel(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalHeader}>
              <div>
                <div style={{ ...S.modalTitle, color: selectedVessel.groupColor }}>{selectedVessel.name}</div>
                <div style={S.modalSubtitle}>{selectedVessel.groupName}</div>
              </div>
              <button style={S.closeBtn} onClick={() => setSelectedVessel(null)}>&times;</button>
            </div>
            <div style={S.modalBody}>
              <div style={S.grid2}>
                <div>
                  <div style={S.fieldLabel}>MMSI</div>
                  <div style={S.fieldValue}>{selectedVessel.mmsi}</div>
                </div>
                {selectedVessel.imo && (
                  <div>
                    <div style={S.fieldLabel}>IMO</div>
                    <div style={S.fieldValue}>{selectedVessel.imo}</div>
                  </div>
                )}
                <div>
                  <div style={S.fieldLabel}>Flag</div>
                  <div style={S.fieldValue}>{selectedVessel.flag === 'USA' ? 'United States' : selectedVessel.flag}</div>
                </div>
                <div>
                  <div style={S.fieldLabel}>Last Seen</div>
                  <div style={S.fieldValue}>{selectedVessel.lastSeen?.toLocaleDateString() || 'Unknown'}</div>
                </div>
                {selectedVessel.lat && selectedVessel.lng && (
                  <>
                    <div>
                      <div style={S.fieldLabel}>Latitude</div>
                      <div style={S.fieldValue}>{selectedVessel.lat.toFixed(4)}&deg;</div>
                    </div>
                    <div>
                      <div style={S.fieldLabel}>Longitude</div>
                      <div style={S.fieldValue}>{selectedVessel.lng.toFixed(4)}&deg;</div>
                    </div>
                  </>
                )}
              </div>
              <div style={{ fontSize: '0.78rem', color: selectedVessel.dataSource === 'gfw-api' ? '#10b981' : '#B8860B', marginBottom: '0.5rem' }}>
                {selectedVessel.dataSource === 'gfw-api' ? '&#10003; Real AIS data from Global Fishing Watch' : '&#9651; Simulated position (no recent AIS data)'}
              </div>
              <a
                href={`https://globalfishingwatch.org/map/vessel/${selectedVessel.mmsi}`}
                target="_blank"
                rel="noopener noreferrer"
                style={S.gfwLink}
              >
                View on Global Fishing Watch &rarr;
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
